import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import type { PieArcProps, PieValueType } from '@mui/x-charts'
import { PieChart } from '@mui/x-charts/PieChart'

import { P } from '@mxenabled/mxui'
import { TrendingDown, TrendingUp, ArrowRightAlt } from '@mxenabled/mx-icons'

import { getMarkerXYCoordinates, radiansToDegrees } from '../../util/finstrongUtils'

import { CustomPieArc } from './CustomPieArc'

export interface DialScoreCardProps {
  arcRadius: number
  healthScore: number
  size: 'L' | 'M' | 'SM'
  dialScoreData: PieValueType[]
  minValue?: number
  maxValue?: number
  margin?: { top?: number; right?: number; left?: number; bottom?: number }
  markerColor?: string
  healthScoreStatus: string
  footerCenterContent?: string | number
  footerLeftContent?: number
  footerRightContent?: number
  isFinacialStrengthGraph?: boolean
}
export const DialScoreCard = ({
  arcRadius = 100,
  healthScore,
  size = 'L',
  dialScoreData,
  minValue = 0,
  maxValue = 100,
  margin,
  markerColor,
  healthScoreStatus,
  footerCenterContent,
  footerLeftContent,
  footerRightContent,
  isFinacialStrengthGraph = false,
}: DialScoreCardProps) => {
  const theme = useTheme()
  const arcStartAngle = -Math.PI / 1.35
  const arcEndAngle = Math.PI / 1.35

  //finding the end angle of the filled(colored) arc i.e progressArc
  const totalAngularSpan = arcEndAngle - arcStartAngle

  const anglePerUnit = totalAngularSpan / (maxValue - minValue)
  const progressEndAngleOffset = (healthScore - minValue) * anglePerUnit
  const progressEndAngle = arcStartAngle + progressEndAngleOffset + 0.02 // Adding a small offset to avoid overlap with the next arc

  const thicknessRatio = size !== 'SM' ? 0.1 : 0.12
  const outterArcRadius = arcRadius
  const innerArcRadius = arcRadius * (1 - thicknessRatio)

  const chartDimenesions = outterArcRadius * 2 //width and height of the chart
  const centerX = chartDimenesions / 2
  const centerY = chartDimenesions / 2
  const markerRadius = (innerArcRadius + outterArcRadius) / 2
  const angleOffset =
    healthScore !== 25 && healthScore !== 50 && healthScore !== 75 && healthScore !== 100 ? 0 : 0.12 // Adjust the offset based on health score

  const [markerX, markerY] = getMarkerXYCoordinates(markerRadius, progressEndAngle, angleOffset)

  const unitsPerSegment = healthScore / 25

  const sizeMap = {
    L: { markerSize: 12, healthScoreFontSize: 64 },
    M: { markerSize: 8, healthScoreFontSize: 48 },
    SM: { markerSize: 3, healthScoreFontSize: 14 },
  }

  const chartMargin = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...margin,
  }
  const PercentageIcon = footerCenterContent === 0 ? ArrowRightAlt : TrendingDown
  const stroke = size !== 'SM' ? 3 : 1
  const padding = stroke * 4 // enough room for stroke
  const svgSize = sizeMap[size].markerSize * 2 + padding

  return (
    <Stack
      sx={{
        width: chartDimenesions,
        height: size === 'SM' ? chartDimenesions - 7 : chartDimenesions,
        position: 'relative',
        alignSelf: 'center',
        overflow: 'visible',
      }}
    >
      <PieChart
        height={size === 'SM' ? chartDimenesions - 7 : chartDimenesions}
        hideLegend={true}
        margin={chartMargin}
        series={[
          {
            data: dialScoreData,
            innerRadius: innerArcRadius,
            outerRadius: outterArcRadius,
            startAngle: Math.round(radiansToDegrees(arcStartAngle)), // Use Math.degrees to convert
            endAngle: Math.round(radiansToDegrees(arcEndAngle)), // Use Math.degrees to convert
          },
        ]}
        skipAnimation={true}
        slots={{
          pieArc: (props: PieArcProps) => (
            <CustomPieArc
              arcColor={props.color}
              arcRadius={arcRadius}
              arcStartAngle={arcStartAngle}
              centerX={centerX}
              centerY={centerY}
              dataIndex={props.dataIndex}
              dialScoreData={dialScoreData}
              innerArcRadius={innerArcRadius}
              isFinacialStrengthGraph={isFinacialStrengthGraph}
              outterArcRadius={outterArcRadius}
              progressEndAngle={progressEndAngle}
              totalAngularSpan={totalAngularSpan}
              unitsPerSegment={unitsPerSegment}
            />
          ),
        }}
        sx={{
          '& path': {
            stroke: 'none',
          },
        }}
        width={chartDimenesions}
      />

      {healthScore >= minValue && healthScore <= maxValue && (
        <svg
          height={svgSize}
          style={{
            position: 'absolute',
            left: centerX + markerX - sizeMap[size].markerSize - 5,
            top: centerY + markerY - sizeMap[size].markerSize - 5,
            overflow: 'visible',
          }}
          viewBox={`${centerX + markerX - sizeMap[size].markerSize - 5} ${centerY + markerY - sizeMap[size].markerSize - 5} ${svgSize} ${svgSize}`}
          width={svgSize}
        >
          <circle
            cx={centerX + markerX}
            cy={centerY + markerY}
            fill={theme.palette.common.white}
            r={sizeMap[size].markerSize}
            stroke={isFinacialStrengthGraph ? 'url(#arcGradient)' : markerColor}
            strokeWidth={stroke}
          />
        </svg>
      )}
      <Box
        sx={{
          position: 'absolute',
          top: `${centerY}px`,
          left: `${centerX}px`,
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        <Stack
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'center', gap: size !== 'M' ? 10 : 3 }}
        >
          <P
            sx={{
              fontSize: sizeMap[size].healthScoreFontSize,
              lineHeight: size !== 'SM' ? '48px' : '100%',
            }}
            variant="h1"
          >
            {healthScore}
          </P>
          {size !== 'SM' && (
            <P sx={{ fontWeight: 600 }} variant={size === 'L' ? 'body1' : 'body2'}>
              {healthScoreStatus}
            </P>
          )}
        </Stack>
      </Box>
      {footerCenterContent !== undefined && (
        <Box
          sx={{
            position: 'absolute',
            top: `${centerY * 2 * 0.83}px`,
            left: `${centerX}px`,
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          {typeof footerCenterContent === 'number' ? (
            <Stack
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                mt: 24,
              }}
            >
              {footerCenterContent > 0 ? (
                <TrendingUp size={16} sx={{ color: 'success.main' }} />
              ) : (
                <PercentageIcon size={16} sx={{ color: 'text.secondary' }} />
              )}
              <P
                sx={{
                  fontWeight: 600,
                  color: footerCenterContent > 0 ? 'success.main' : 'text.secondary',
                }}
                variant="body1"
              >
                {Math.abs(footerCenterContent)}
              </P>
            </Stack>
          ) : (
            <P sx={{ color: 'text.secondary' }} variant={'subtitle2'}>
              {footerCenterContent}
            </P>
          )}
        </Box>
      )}
      {/* Left Footer Content */}
      {footerLeftContent !== undefined && (
        <Box
          sx={{
            position: 'absolute',
            top: `${centerY * 2 * 0.83}px`,
            left: `${centerX - centerX / 2}px`, // shift left from center
            transform: 'translate(-50%, -50%)',
            textAlign: 'left',
          }}
        >
          <P variant="body1">{footerLeftContent}</P>
        </Box>
      )}

      {/* Right Footer Content */}
      {footerRightContent !== undefined && (
        <Box
          sx={{
            position: 'absolute',
            top: `${centerY * 2 * 0.83}px`,
            left: `${centerX + centerX / 2}px`,
            transform: 'translate(-50%, -50%)',
            textAlign: 'right',
          }}
        >
          <P variant="body1">{footerRightContent}</P>
        </Box>
      )}
    </Stack>
  )
}
